import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import "./index.css";
import {
  Select,
  Input,
  Checkbox,
  DatePicker,
  Dropdown,
  Modal,
  Spin,
  Tooltip,
} from "antd";
import type { MenuProps } from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  MessageOutlined,
  SendOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  LinkOutlined,
  PaperClipOutlined,
  EditOutlined,
  CloseOutlined,
  SaveOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
// @ts-ignore
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getBoard,
  createTask,
  updateTask,
  moveTask,
  deleteTask,
  reorderColumns,
  getTaskComments,
  postComment,
  getMilestones,
  getDeadlines,
  getAttachments,
  addAttachment,
  deleteAttachment,
  getAssignees,
} from "./projectHub.service";
import type {
  ApiTask,
  ApiTaskWithDeadline,
  ApiColumn,
  ApiComment,
  ApiAttachment,
  ApiPriority,
  ApiMilestonesSummary,
} from "./projectHub.service";

// ── Constants ─────────────────────────────────────────────────────────────────

const PRIORITY_CONFIG: Record<ApiPriority, { color: string; bg: string }> = {
  low: { color: "#6b7280", bg: "#f3f4f6" },
  medium: { color: "#3b82f6", bg: "#eff6ff" },
  high: { color: "#f97316", bg: "#fff7ed" },
  urgent: { color: "#ef4444", bg: "#fef2f2" },
};

const LINKED_TOOLS = [
  "AI Translation",
  "Data Cleaning",
  "Data Anonymisation",
  "Document Refining",
  "Document Insights",
  "Dashboard",
];

const AVATAR_COLORS = [
  "#8b5cf6",
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
];

function avatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

function getDueDateInfo(
  dueDate: string | null,
  t: TFunction,
): { text: string; color: string } | null {
  if (!dueDate) return null;
  const today = dayjs().startOf("day");
  const due = dayjs(dueDate).startOf("day");
  const diff = due.diff(today, "day");
  if (diff < 0)
    return {
      text: `${t("common.overdue")} • ${due.format("MMM D, YYYY")}`,
      color: "#ef4444",
    };
  if (diff === 0)
    return {
      text: `${t("dueDate.dueToday")} • ${due.format("MMM D, YYYY")}`,
      color: "#f97316",
    };
  if (diff <= 3)
    return {
      text: `${t("dueDate.dueInDays", { count: diff })} • ${due.format("MMM D, YYYY")}`,
      color: "#f97316",
    };
  return { text: due.format("MMMM D, YYYY"), color: "#6b7280" };
}

// ── Component ─────────────────────────────────────────────────────────────────

const ProjectHub: React.FC = () => {
  const { t } = useTranslation("projectHub");
  const priorityLabel = (p: ApiPriority) => t(`priority.${p}`);
  const { id: projectId } = useParams<{ id: string }>();

  // ── Board state
  const [columns, setColumns] = useState<ApiColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [boardError, setBoardError] = useState<string | null>(null);

  // ── Tab state
  const [activeTab, setActiveTab] = useState<
    "board" | "milestones" | "deadlines"
  >("board");
  const [milestonesSummary, setMilestonesSummary] =
    useState<ApiMilestonesSummary | null>(null);
  const [milestoneItems, setMilestoneItems] = useState<ApiTaskWithDeadline[]>(
    [],
  );
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [deadlineItems, setDeadlineItems] = useState<ApiTaskWithDeadline[]>([]);
  const [deadlinesLoading, setDeadlinesLoading] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // ── Delete modal
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  // ── Filters
  const [filterAssignee, setFilterAssignee] = useState<string | undefined>(
    undefined,
  );
  const [filterPriority, setFilterPriority] = useState<ApiPriority | undefined>(
    undefined,
  );
  const [filterTool, setFilterTool] = useState<string | undefined>(undefined);

  // ── Task preview
  const [selectedTask, setSelectedTask] = useState<ApiTask | null>(null);
  const [taskComments, setTaskComments] = useState<ApiComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commenting, setCommenting] = useState(false);

  // ── Attachments
  const [attachments, setAttachments] = useState<ApiAttachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [showAddAttach, setShowAddAttach] = useState(false);
  const [newAttachName, setNewAttachName] = useState("");
  const [newAttachUrl, setNewAttachUrl] = useState("");
  const [addingAttach, setAddingAttach] = useState(false);

  // ── Edit mode
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editStatusId, setEditStatusId] = useState("");
  const [editPriority, setEditPriority] = useState<ApiPriority>("medium");
  const [editMilestone, setEditMilestone] = useState(false);
  const [editDueDate, setEditDueDate] = useState<Dayjs | null>(null);
  const [editAssigneeId, setEditAssigneeId] = useState<number | undefined>(
    undefined,
  );
  const [editLinkedTool, setEditLinkedTool] = useState<string | undefined>(
    undefined,
  );
  const [editDescription, setEditDescription] = useState("");

  // ── Add task form
  const [showAddTask, setShowAddTask] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newStatusId, setNewStatusId] = useState("");
  const [newPriority, setNewPriority] = useState<ApiPriority | undefined>(
    undefined,
  );
  const [newMilestone, setNewMilestone] = useState(false);
  const [newDueDate, setNewDueDate] = useState<Dayjs | null>(null);
  const [newAssigneeId, setNewAssigneeId] = useState<number | undefined>(
    undefined,
  );
  const [newLinkedTool, setNewLinkedTool] = useState<string | undefined>(
    undefined,
  );
  const [newDescription, setNewDescription] = useState("");

  // ── Rich text editor ──────────────────────────────────────────────────────

  const newDescRef = useRef<any>(null);
  const editDescRef = useRef<any>(null);
  const quillModules = useMemo(() => ({ toolbar: false }), []);

  const formatDesc = (ref: React.MutableRefObject<any>, format: string) => {
    const quill = ref.current?.getEditor();
    if (!quill) return;
    quill.format(format, !quill.getFormat()[format]);
    quill.focus();
  };

  const insertLinkDesc = (ref: React.MutableRefObject<any>) => {
    const url = window.prompt(t("common.enterUrlPrompt"));
    if (!url) return;
    const quill = ref.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true);
    if (range.length > 0) {
      quill.format("link", url);
    } else {
      quill.insertText(range.index, url, "link", url);
    }
    quill.focus();
  };

  const insertImageDesc = (ref: React.MutableRefObject<any>) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const quill = ref.current?.getEditor();
        const range = quill?.getSelection(true);
        quill?.insertEmbed(range?.index ?? 0, "image", reader.result);
      };
      reader.readAsDataURL(file);
    };
  };

  const cleanDesc = (html: string) =>
    html === "<p><br></p>" || html === "" ? "" : html;

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  const fetchBoard = useCallback(async () => {
    if (!projectId) {
      setBoardError(t("errors.noProjectId", { projectId }));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setBoardError(null);
      const params: Parameters<typeof getBoard>[1] = {};
      if (filterAssignee) params.assignee_user_id = Number(filterAssignee);
      if (filterPriority) params.priority = filterPriority;
      if (filterTool) params.linked_tool = filterTool;
      const board = await getBoard(projectId, params);
      setColumns(board.columns);
      setNewStatusId((prev) => prev || board.columns[0]?.id || "");
    } catch (err: any) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.detail ?? err?.message ?? t("errors.unknownError");
      setBoardError(
        t("errors.boardErrorDetail", { status: status ?? "?", msg, projectId }),
      );
      toast.error(t("errors.boardLoadFailed", { status: status ?? "?" }));
    } finally {
      setLoading(false);
    }
  }, [projectId, filterAssignee, filterPriority, filterTool]);

  const fetchMilestones = useCallback(async () => {
    if (!projectId) return;
    setMilestonesLoading(true);
    try {
      const params: Parameters<typeof getMilestones>[1] = {};
      if (filterAssignee) params.assignee_user_id = Number(filterAssignee);
      if (filterPriority) params.priority = filterPriority;
      if (filterTool) params.linked_tool = filterTool;
      const res = await getMilestones(projectId, params);
      setMilestonesSummary(res.summary);
      setMilestoneItems(res.milestones);
    } catch {
      toast.error(t("errors.loadMilestonesFailed"));
    } finally {
      setMilestonesLoading(false);
    }
  }, [projectId, filterAssignee, filterPriority, filterTool]);

  const fetchDeadlines = useCallback(async () => {
    if (!projectId) return;
    setDeadlinesLoading(true);
    try {
      const params: Parameters<typeof getDeadlines>[1] = {};
      if (filterAssignee) params.assignee_user_id = Number(filterAssignee);
      if (filterPriority) params.priority = filterPriority;
      if (filterTool) params.linked_tool = filterTool;
      const res = await getDeadlines(projectId, params);
      setDeadlineItems(res);
    } catch {
      toast.error(t("errors.loadDeadlinesFailed"));
    } finally {
      setDeadlinesLoading(false);
    }
  }, [projectId, filterAssignee, filterPriority, filterTool]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    if (activeTab === "milestones") fetchMilestones();
    if (activeTab === "deadlines") fetchDeadlines();
  }, [activeTab, fetchMilestones, fetchDeadlines]);

  // ── Open task ─────────────────────────────────────────────────────────────

  const openTask = async (task: ApiTask) => {
    setSelectedTask(task);
    setTaskComments([]);
    setAttachments([]);
    setEditMode(false);
    setEditTitle(task.title);
    setEditStatusId(task.status_id ?? "");
    setEditPriority(task.priority);
    setEditMilestone(task.is_milestone);
    setEditDueDate(task.due_date ? dayjs(task.due_date) : null);
    setEditAssigneeId(task.assignee?.id);
    setEditLinkedTool(task.linked_tool ?? undefined);
    setEditDescription(task.description ?? "");
    if (!projectId) return;
    setCommentsLoading(true);
    setAttachmentsLoading(true);
    try {
      const [commentsRes, attachRes] = await Promise.all([
        getTaskComments(projectId, task.id),
        getAttachments(projectId, task.id),
      ]);
      setTaskComments(commentsRes.comments);
      setAttachments(attachRes);
    } catch {
      toast.error(t("errors.loadTaskDetailsFailed"));
    } finally {
      setCommentsLoading(false);
      setAttachmentsLoading(false);
    }
  };

  const closeTask = () => {
    setSelectedTask(null);
    setTaskComments([]);
    setAttachments([]);
    setEditMode(false);
    setShowAddAttach(false);
    setNewAttachName("");
    setNewAttachUrl("");
  };

  // ── Assignees (from dedicated API endpoint) ──────────────────────────────
  const [assigneeOptions, setAssigneeOptions] = useState<
    { id: number; username: string }[]
  >([]);

  useEffect(() => {
    if (!projectId) return;
    getAssignees(projectId)
      .then(setAssigneeOptions)
      .catch(() => {});
  }, [projectId]);

  // ── DnD ───────────────────────────────────────────────────────────────────

  const onDragEnd = async (result: DropResult) => {
    const { draggableId, source, destination, type } = result;
    if (!destination || !projectId) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;
    if (type === "COLUMN") {
      const colId = draggableId.replace("col-", "");
      const newCols = [...columns];
      const fromIdx = newCols.findIndex((c) => c.id === colId);
      const [moved] = newCols.splice(fromIdx, 1);
      newCols.splice(destination.index, 0, moved);
      setColumns(newCols);
      try {
        await reorderColumns(
          projectId,
          newCols.map((c, i) => ({ id: c.id, position: i })),
        );
      } catch {
        toast.error(t("errors.reorderColumnsFailed"));
        fetchBoard();
      }
    } else {
      // TASK drag
      const srcCol = columns.find((c) => c.id === source.droppableId);
      const task = srcCol?.tasks[source.index];
      if (!task) return;

      setColumns((prev) => {
        const updated = prev.map((col) => ({ ...col, tasks: [...col.tasks] }));
        const srcIdx = updated.findIndex((c) => c.id === source.droppableId);
        const dstIdx = updated.findIndex(
          (c) => c.id === destination.droppableId,
        );
        const [movedTask] = updated[srcIdx].tasks.splice(source.index, 1);
        movedTask.status_id = destination.droppableId;
        updated[dstIdx].tasks.splice(destination.index, 0, movedTask);
        return updated;
      });

      try {
        await moveTask(
          projectId,
          task.id,
          destination.droppableId,
          destination.index,
        );
      } catch {
        toast.error(t("errors.moveTaskFailed"));
        fetchBoard();
      }
    }
  };

  // ── Mutations ─────────────────────────────────────────────────────────────

  const resetAddForm = () => {
    setNewTitle("");
    setNewStatusId(columns[0]?.id ?? "");
    setNewPriority(undefined);
    setNewMilestone(false);
    setNewDueDate(null);
    setNewAssigneeId(undefined);
    setNewLinkedTool(undefined);
    setNewDescription("");
  };

  const handleCreateTask = async () => {
    if (!newTitle.trim() || !projectId) return;
    setCreating(true);
    try {
      await createTask(projectId, {
        title: newTitle.trim(),
        description: cleanDesc(newDescription) || undefined,
        priority: newPriority,
        is_milestone: newMilestone,
        due_date: newDueDate ? newDueDate.format("YYYY-MM-DD") : undefined,
        linked_tool: newLinkedTool || undefined,
        status_id: newStatusId || undefined,
        assignee_user_id: newAssigneeId,
      });
      await fetchBoard();
      resetAddForm();
      setShowAddTask(false);
      toast.success(t("success.taskCreated"));
    } catch {
      toast.error(t("errors.createTaskFailed"));
    } finally {
      setCreating(false);
    }
  };

  const handleSaveTask = async () => {
    if (!selectedTask || !projectId) return;
    setSaving(true);
    try {
      const updated = await updateTask(projectId, selectedTask.id, {
        title: editTitle,
        description: cleanDesc(editDescription) || undefined,
        priority: editPriority,
        is_milestone: editMilestone,
        due_date: editDueDate ? editDueDate.format("YYYY-MM-DD") : null,
        linked_tool: editLinkedTool ?? null,
        status_id: editStatusId || undefined,
        assignee_user_id: editAssigneeId ?? null,
      });
      setSelectedTask(updated);
      setEditMode(false);
      const statusChanged = updated.status_id !== selectedTask.status_id;
      setColumns((prev) => {
        if (!statusChanged) {
          return prev.map((col) => ({
            ...col,
            tasks: col.tasks.map((t) => (t.id === updated.id ? updated : t)),
          }));
        }
        return prev.map((col) => {
          if (col.id === selectedTask.status_id) {
            return { ...col, tasks: col.tasks.filter((t) => t.id !== updated.id) };
          }
          if (col.id === updated.status_id) {
            return { ...col, tasks: [...col.tasks, updated] };
          }
          return col;
        });
      });
      toast.success(t("success.taskUpdated"));
    } catch {
      toast.error(t("errors.updateTaskFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = (taskId: string) => {
    setDeleteTargetId(taskId);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId || !projectId) return;
    setDeleteConfirmLoading(true);
    try {
      await deleteTask(projectId, deleteTargetId);
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          tasks: col.tasks.filter((t) => t.id !== deleteTargetId),
        })),
      );
      setMilestoneItems((prev) => prev.filter((t) => t.id !== deleteTargetId));
      setDeadlineItems((prev) => prev.filter((t) => t.id !== deleteTargetId));
      if (selectedTask?.id === deleteTargetId) closeTask();
      setDeleteTargetId(null);
    } catch {
      toast.error(t("errors.deleteTaskFailed"));
    } finally {
      setDeleteConfirmLoading(false);
    }
  };

  const handleSendComment = async () => {
    if (!commentText.trim() || !selectedTask || !projectId) return;
    setCommenting(true);
    try {
      const comment = await postComment(
        projectId,
        selectedTask.id,
        commentText.trim(),
      );
      setTaskComments((prev) => [...prev, comment]);
      setCommentText("");
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          tasks: col.tasks.map((t) =>
            t.id === selectedTask.id
              ? { ...t, comments_count: t.comments_count + 1 }
              : t,
          ),
        })),
      );
    } catch {
      toast.error(t("errors.sendCommentFailed"));
    } finally {
      setCommenting(false);
    }
  };

  const handleAddAttachment = async () => {
    if (
      !newAttachName.trim() ||
      !newAttachUrl.trim() ||
      !selectedTask ||
      !projectId
    )
      return;
    setAddingAttach(true);
    try {
      const attach = await addAttachment(projectId, selectedTask.id, {
        file_name: newAttachName.trim(),
        url: newAttachUrl.trim(),
      });
      setAttachments((prev) => [...prev, attach]);
      setNewAttachName("");
      setNewAttachUrl("");
      setShowAddAttach(false);
    } catch {
      toast.error(t("errors.addAttachmentFailed"));
    } finally {
      setAddingAttach(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!selectedTask || !projectId) return;
    try {
      await deleteAttachment(projectId, selectedTask.id, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch {
      toast.error(t("errors.deleteAttachmentFailed"));
    }
  };

  // ── Task card ─────────────────────────────────────────────────────────────

  const renderTaskCard = (task: ApiTask) => {
    const dateInfo = getDueDateInfo(task.due_date, t);
    const prioConf = PRIORITY_CONFIG[task.priority] ?? PRIORITY_CONFIG.medium;
    const prioLabel = priorityLabel(task.priority);

    const cardMenu: MenuProps = {
      items: [
        {
          key: "delete",
          label: t("common.delete"),
          danger: true,
          onClick: (e) => {
            e.domEvent.stopPropagation();
            handleDeleteTask(task.id);
          },
        },
      ],
    };

    return (
      <div className="ph-task-card" onClick={() => openTask(task)}>
        <div className="ph-card-top">
          <div className="ph-card-badges">
            <span
              className="ph-priority-badge"
              style={{ color: prioConf.color, background: prioConf.bg }}
            >
              {prioLabel}
            </span>
            {task.linked_tool && (
              <span className="ph-tool-badge">{task.linked_tool}</span>
            )}
          </div>
          <Dropdown menu={cardMenu} trigger={["click"]}>
            <button
              className="ph-more-btn"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
        <div className="ph-card-title">
          {task.is_milestone && <span className="ph-diamond">♦</span>}
          {task.title}
        </div>
        {(dateInfo || task.created_at) && (
          <div
            className="ph-card-date"
            style={{ color: dateInfo?.color ?? "#6b7280" }}
          >
            {dateInfo
              ? dateInfo.text
              : dayjs(task.created_at).format("MMMM D, YYYY")}
          </div>
        )}
        <div className="ph-card-footer">
          <div className="ph-avatars">
            {task.assignee && (
              <Tooltip title={task.assignee.username}>
                <div
                  className="ph-avatar"
                  style={{ background: avatarColor(task.assignee.id) }}
                >
                  {task.assignee.username.charAt(0).toUpperCase()}
                </div>
              </Tooltip>
            )}
          </div>
          <div className="ph-comment-count">
            <MessageOutlined />
            <span>{task.comments_count ?? 0}</span>
          </div>
        </div>
      </div>
    );
  };

  // ── Board ─────────────────────────────────────────────────────────────────

  const renderBoard = () => {
    if (loading)
      return (
        <div className="ph-loading">
          <Spin size="large" />
        </div>
      );

    if (boardError || columns.length === 0) {
      return (
        <div className="ph-board-empty-state">
          <div className="ph-board-empty-icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
              <rect
                x="2"
                y="3"
                width="20"
                height="18"
                rx="3"
                stroke="#d1d5db"
                strokeWidth="1.5"
              />
              <path
                d="M8 7h8M8 11h8M8 15h5"
                stroke="#d1d5db"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p className="ph-board-empty-title">{t("board.emptyTitle")}</p>
          {boardError && (
            <p
              style={{
                fontSize: 12,
                color: "#ef4444",
                margin: 0,
                fontFamily: "monospace",
                maxWidth: 480,
                wordBreak: "break-all",
                textAlign: "center",
              }}
            >
              {boardError}
            </p>
          )}
          <p className="ph-board-empty-sub">
            {t("board.emptySubtitle")}
          </p>
          <button
            className="ph-add-task-btn"
            style={{ marginTop: 8 }}
            onClick={fetchBoard}
          >
            {t("board.retry")}
          </button>
        </div>
      );
    }

    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="ph-board-container">
          <Droppable
            droppableId="board-columns"
            direction="horizontal"
            type="COLUMN"
          >
            {(colsProvided) => (
              <div
                className="ph-board-columns"
                ref={colsProvided.innerRef}
                {...colsProvided.droppableProps}
              >
                {columns.map((col, colIndex) => {
                  const colMenu: MenuProps = {
                    items: [{ key: "clear", label: t("board.clearColumn") }],
                  };
                  return (
                    <Draggable
                      key={col.id}
                      draggableId={`col-${col.id}`}
                      index={colIndex}
                      isDragDisabled={false}
                    >
                      {(colDraggable) => (
                        <div
                          className="ph-column"
                          ref={colDraggable.innerRef}
                          {...colDraggable.draggableProps}
                        >
                          {/* Drag handle is the column header */}
                          <div
                            className="ph-column-header"
                            {...colDraggable.dragHandleProps}
                          >
                            <div className="ph-column-header-left">
                              <span
                                className="ph-column-dot"
                                style={{ background: col.color }}
                              />
                              <span className="ph-column-name">{col.name}</span>
                              <span className="ph-column-count">
                                {col.tasks.length}
                              </span>
                            </div>
                            <Dropdown menu={colMenu} trigger={["click"]}>
                              <button
                                className="ph-more-btn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreOutlined />
                              </button>
                            </Dropdown>
                          </div>

                          <Droppable droppableId={col.id} type="TASK">
                            {(tasksProvided, snapshot) => (
                              <div
                                className={`ph-column-body ${snapshot.isDraggingOver ? "ph-column-body--over" : ""}`}
                                ref={tasksProvided.innerRef}
                                {...tasksProvided.droppableProps}
                              >
                                {col.tasks.length === 0 &&
                                  !snapshot.isDraggingOver && (
                                    <div className="ph-column-empty">
                                      <div className="ph-column-empty-icon">
                                        <svg
                                          width="32"
                                          height="32"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                        >
                                          <rect
                                            x="3"
                                            y="5"
                                            width="18"
                                            height="14"
                                            rx="2"
                                            stroke="#d1d5db"
                                            strokeWidth="1.5"
                                          />
                                          <path
                                            d="M8 10h8M8 14h5"
                                            stroke="#d1d5db"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                          />
                                        </svg>
                                      </div>
                                      <p className="ph-column-empty-text">
                                        {t("board.noTasks")}
                                      </p>
                                      <button
                                        className="ph-column-empty-add"
                                        onClick={() => {
                                          setNewStatusId(col.id);
                                          setShowAddTask(true);
                                        }}
                                      >
                                        + {t("common.addTask")}
                                      </button>
                                    </div>
                                  )}
                                {col.tasks.map((task, taskIndex) => (
                                  <Draggable
                                    key={task.id}
                                    draggableId={task.id}
                                    index={taskIndex}
                                    isDragDisabled={false}
                                  >
                                    {(taskDraggable, taskSnapshot) => (
                                      <div
                                        ref={taskDraggable.innerRef}
                                        {...taskDraggable.draggableProps}
                                        {...taskDraggable.dragHandleProps}
                                        style={{
                                          ...taskDraggable.draggableProps.style,
                                          opacity: taskSnapshot.isDragging
                                            ? 0.85
                                            : 1,
                                        }}
                                      >
                                        {renderTaskCard(task)}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {tasksProvided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {colsProvided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    );
  };

  // ── Shared task table row ─────────────────────────────────────────────────

  const renderTaskRow = (task: ApiTask | ApiTaskWithDeadline, showDiamond = false) => {
    const dateInfo = getDueDateInfo(task.due_date, t);
    const prioConf = PRIORITY_CONFIG[task.priority] ?? PRIORITY_CONFIG.medium;
    const col = columns.find((c) => c.id === task.status_id);
    const rowMenu: MenuProps = {
      items: [{ key: "delete", label: t("common.delete"), danger: true, onClick: () => setDeleteTargetId(task.id) }],
    };
    return (
      <div key={task.id} className="ph-task-row" onClick={() => openTask(task)}>
        <div className="ph-table-col ph-table-col--task">
          {showDiamond && <span className="ph-diamond">♦</span>}
          <span className="ph-task-row-title">{task.title}</span>
        </div>
        <div className="ph-table-col ph-table-col--priority">
          <span className="ph-priority-badge" style={{ color: prioConf.color, background: prioConf.bg }}>
            {priorityLabel(task.priority)}
          </span>
        </div>
        <div className="ph-table-col ph-table-col--assignee">
          {task.assignee ? (
            <Tooltip title={task.assignee.username}>
              <div className="ph-avatar" style={{ background: avatarColor(task.assignee.id) }}>
                {task.assignee.username.charAt(0).toUpperCase()}
              </div>
            </Tooltip>
          ) : <span className="ph-table-empty">—</span>}
        </div>
        <div className="ph-table-col ph-table-col--due" style={{ color: dateInfo?.color ?? "#6b7280" }}>
          {task.due_date
            ? (dateInfo ? dateInfo.text : dayjs(task.due_date).format("MMM D, YYYY"))
            : <span className="ph-table-empty">—</span>}
        </div>
        <div className="ph-table-col ph-table-col--tool">
          {task.linked_tool ?? <span className="ph-table-empty">—</span>}
        </div>
        <div className="ph-table-col ph-table-col--status">
          {col ? (
            <span style={{ display: "flex", alignItems: "center", gap: 5, color: col.color }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0, display: "inline-block" }} />
              {col.name}
            </span>
          ) : <span className="ph-table-empty">—</span>}
        </div>
        <div className="ph-table-col ph-table-col--actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown menu={rowMenu} trigger={["click"]}>
            <button className="ph-more-btn"><MoreOutlined /></button>
          </Dropdown>
        </div>
      </div>
    );
  };

  // ── Milestones ────────────────────────────────────────────────────────────

  const renderMilestones = () => {
    if (milestonesLoading)
      return <div className="ph-loading"><Spin size="large" /></div>;
    return (
      <div className="ph-list-view">
        {milestonesSummary && (
          <div className="ph-milestone-summary">
            <div className="ph-summary-item ph-summary-item--green">
              <span className="ph-summary-num">{milestonesSummary.on_track}</span>
              <span className="ph-summary-label">{t("milestones.onTrack")}</span>
            </div>
            <div className="ph-summary-item ph-summary-item--orange">
              <span className="ph-summary-num">{milestonesSummary.due_this_week}</span>
              <span className="ph-summary-label">{t("milestones.dueIn7Days")}</span>
            </div>
            <div className="ph-summary-item ph-summary-item--red">
              <span className="ph-summary-num">{milestonesSummary.overdue}</span>
              <span className="ph-summary-label">{t("common.overdue")}</span>
            </div>
          </div>
        )}
        {milestoneItems.length === 0 ? (
          <div className="ph-empty"><p>{t("milestones.empty")}</p></div>
        ) : (
          <div className="ph-milestone-cards">
            <div className="ph-milestone-cards-header">
              <div className="ph-table-col ph-table-col--task">{t("table.columns.task")}</div>
              <div className="ph-table-col ph-table-col--priority">{t("table.columns.priority")}</div>
              <div className="ph-table-col ph-table-col--assignee">{t("table.columns.assignee")}</div>
              <div className="ph-table-col ph-table-col--due">{t("table.columns.dueDate")}</div>
              <div className="ph-table-col ph-table-col--tool">{t("table.columns.linkedTool")}</div>
              <div className="ph-table-col ph-table-col--status">{t("table.columns.status")}</div>
              <div className="ph-table-col ph-table-col--actions" />
            </div>
            {milestoneItems.map((task) => {
              const dateInfo = getDueDateInfo(task.due_date, t);
              const prioConf = PRIORITY_CONFIG[task.priority] ?? PRIORITY_CONFIG.medium;
              const col = columns.find((c) => c.id === task.status_id);
              const rowMenu: MenuProps = {
                items: [{ key: "delete", label: t("common.delete"), danger: true, onClick: () => setDeleteTargetId(task.id) }],
              };
              return (
                <div key={task.id} className="ph-milestone-card" onClick={() => openTask(task)}>
                  <div className="ph-table-col ph-table-col--task">
                    <span className="ph-diamond">♦</span>
                    <span className="ph-task-row-title">{task.title}</span>
                  </div>
                  <div className="ph-table-col ph-table-col--priority">
                    <span className="ph-priority-badge" style={{ color: prioConf.color, background: prioConf.bg }}>
                      {priorityLabel(task.priority)}
                    </span>
                  </div>
                  <div className="ph-table-col ph-table-col--assignee">
                    {task.assignee ? (
                      <Tooltip title={task.assignee.username}>
                        <div className="ph-avatar" style={{ background: avatarColor(task.assignee.id) }}>
                          {task.assignee.username.charAt(0).toUpperCase()}
                        </div>
                      </Tooltip>
                    ) : <span className="ph-table-empty">—</span>}
                  </div>
                  <div className="ph-table-col ph-table-col--due" style={{ color: dateInfo?.color ?? "#6b7280" }}>
                    {task.due_date
                      ? (dateInfo ? dateInfo.text : dayjs(task.due_date).format("MMM D, YYYY"))
                      : <span className="ph-table-empty">—</span>}
                  </div>
                  <div className="ph-table-col ph-table-col--tool">
                    {task.linked_tool ?? <span className="ph-table-empty">—</span>}
                  </div>
                  <div className="ph-table-col ph-table-col--status">
                    {col ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 5, color: col.color }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0, display: "inline-block" }} />
                        {col.name}
                      </span>
                    ) : <span className="ph-table-empty">—</span>}
                  </div>
                  <div className="ph-table-col ph-table-col--actions" onClick={(e) => e.stopPropagation()}>
                    <Dropdown menu={rowMenu} trigger={["click"]}>
                      <button className="ph-more-btn"><MoreOutlined /></button>
                    </Dropdown>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Deadlines ─────────────────────────────────────────────────────────────

  const renderDeadlines = () => {
    if (deadlinesLoading)
      return <div className="ph-loading"><Spin size="large" /></div>;
    if (deadlineItems.length === 0)
      return <div className="ph-empty"><p>{t("deadlines.empty")}</p></div>;

    const GROUP_META: Record<string, { color: string; bg: string; badgeBg: string }> = {
      overdue: { color: "#ef4444", bg: "#fef2f2", badgeBg: "#fecaca" },
      week:    { color: "#f97316", bg: "#fff7ed", badgeBg: "#fed7aa" },
      month:   { color: "#374151", bg: "#f9fafb", badgeBg: "#e5e7eb" },
      later:   { color: "#374151", bg: "#f9fafb", badgeBg: "#e5e7eb" },
    };

    const groups = [
      { key: "overdue", label: t("common.overdue"),          items: deadlineItems.filter((task) => task.is_overdue) },
      { key: "week",    label: t("deadlines.groups.week"),    items: deadlineItems.filter((task) => !task.is_overdue && task.days_remaining !== null && task.days_remaining <= 7) },
      { key: "month",   label: t("deadlines.groups.month"),   items: deadlineItems.filter((task) => !task.is_overdue && task.days_remaining !== null && task.days_remaining > 7 && task.days_remaining <= 30) },
      { key: "later",   label: t("deadlines.groups.later"),   items: deadlineItems.filter((task) => !task.is_overdue && (task.days_remaining === null || task.days_remaining > 30)) },
    ].filter((g) => g.items.length > 0);

    return (
      <div className="ph-list-view">
        {groups.map((group) => {
          const collapsed = !!collapsedGroups[group.key];
          const meta = GROUP_META[group.key];
          return (
            <div key={group.key} className="ph-deadline-group">
              <button
                className="ph-deadline-group-header"
                style={{ background: meta.bg }}
                onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group.key]: !collapsed }))}
              >
                <span className={`ph-group-arrow ${collapsed ? "ph-group-arrow--collapsed" : ""}`} style={{ color: meta.color }}>▾</span>
                <span className="ph-group-label" style={{ color: meta.color }}>{group.label}</span>
                <span className="ph-group-count" style={{ background: meta.badgeBg, color: meta.color }}>{group.items.length}</span>
              </button>
              {!collapsed && (
                <div className="ph-deadline-cards">
                  <div className="ph-milestone-cards-header">
                    <div className="ph-table-col ph-table-col--task">{t("table.columns.task")}</div>
                    <div className="ph-table-col ph-table-col--priority">{t("table.columns.priority")}</div>
                    <div className="ph-table-col ph-table-col--assignee">{t("table.columns.assignee")}</div>
                    <div className="ph-table-col ph-table-col--due">{t("table.columns.dueDate")}</div>
                    <div className="ph-table-col ph-table-col--tool">{t("table.columns.linkedTool")}</div>
                    <div className="ph-table-col ph-table-col--status">{t("table.columns.status")}</div>
                    <div className="ph-table-col ph-table-col--actions" />
                  </div>
                  {group.items.map((task) => {
                    const dateInfo = getDueDateInfo(task.due_date, t);
                    const prioConf = PRIORITY_CONFIG[task.priority] ?? PRIORITY_CONFIG.medium;
                    const col = columns.find((c) => c.id === task.status_id);
                    const rowMenu: MenuProps = {
                      items: [{ key: "delete", label: t("common.delete"), danger: true, onClick: () => setDeleteTargetId(task.id) }],
                    };
                    return (
                      <div key={task.id} className="ph-milestone-card" onClick={() => openTask(task)}>
                        <div className="ph-table-col ph-table-col--task">
                          {task.is_milestone && <span className="ph-diamond">♦</span>}
                          <span className="ph-task-row-title">{task.title}</span>
                        </div>
                        <div className="ph-table-col ph-table-col--priority">
                          <span className="ph-priority-badge" style={{ color: prioConf.color, background: prioConf.bg }}>
                            {priorityLabel(task.priority)}
                          </span>
                        </div>
                        <div className="ph-table-col ph-table-col--assignee">
                          {task.assignee ? (
                            <Tooltip title={task.assignee.username}>
                              <div className="ph-avatar" style={{ background: avatarColor(task.assignee.id) }}>
                                {task.assignee.username.charAt(0).toUpperCase()}
                              </div>
                            </Tooltip>
                          ) : <span className="ph-table-empty">—</span>}
                        </div>
                        <div className="ph-table-col ph-table-col--due" style={{ color: dateInfo?.color ?? "#6b7280" }}>
                          {task.due_date
                            ? (dateInfo ? dateInfo.text : dayjs(task.due_date).format("MMM D, YYYY"))
                            : <span className="ph-table-empty">—</span>}
                        </div>
                        <div className="ph-table-col ph-table-col--tool">
                          {task.linked_tool ?? <span className="ph-table-empty">—</span>}
                        </div>
                        <div className="ph-table-col ph-table-col--status">
                          {col ? (
                            <span style={{ display: "flex", alignItems: "center", gap: 5, color: col.color }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0, display: "inline-block" }} />
                              {col.name}
                            </span>
                          ) : <span className="ph-table-empty">—</span>}
                        </div>
                        <div className="ph-table-col ph-table-col--actions" onClick={(e) => e.stopPropagation()}>
                          <Dropdown menu={rowMenu} trigger={["click"]}>
                            <button className="ph-more-btn"><MoreOutlined /></button>
                          </Dropdown>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Add Task modal ────────────────────────────────────────────────────────

  const renderAddTaskModal = () => (
    <Modal
      open={showAddTask}
      onCancel={() => {
        setShowAddTask(false);
        resetAddForm();
      }}
      footer={null}
      title={null}
      width={480}
      className="ph-antd-modal"
      destroyOnClose
    >
      <Input
        className="ph-new-title-input"
        placeholder={t("addTaskModal.titlePlaceholder")}
        value={newTitle}
        onChange={(e) => setNewTitle(e.target.value)}
      />
      <div className="ph-form-grid">
        <div className="ph-form-row">
          <span className="ph-field-label">{t("fields.status")}</span>
          <Select
            value={newStatusId}
            onChange={setNewStatusId}
            className="ph-field-select"
            popupMatchSelectWidth={false}
            options={columns.map((c) => ({
              value: c.id,
              label: (
                <span>
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: c.color,
                      marginRight: 6,
                    }}
                  />
                  {c.name}
                </span>
              ),
            }))}
          />
        </div>
        <div className="ph-form-row">
          <span className="ph-field-label">{t("fields.priority")}</span>
          <Select
            value={newPriority}
            onChange={setNewPriority}
            className="ph-field-select"
            placeholder={t("common.empty")}
            allowClear
            popupMatchSelectWidth={false}
            options={(["low", "medium", "high", "urgent"] as ApiPriority[]).map(
              (p) => ({
                value: p,
                label: (
                  <span
                    style={{ color: PRIORITY_CONFIG[p].color, fontWeight: 600 }}
                  >
                    {priorityLabel(p)}
                  </span>
                ),
              }),
            )}
          />
        </div>
        <div className="ph-form-row">
          <Checkbox
            checked={newMilestone}
            onChange={(e) => setNewMilestone(e.target.checked)}
          >
            {t("fields.milestone")}
          </Checkbox>
        </div>
        <div className="ph-form-row">
          <span className="ph-field-label">{t("fields.dueDate")}</span>
          <DatePicker
            value={newDueDate}
            onChange={setNewDueDate}
            placeholder={t("common.empty")}
            className="ph-field-date"
          />
        </div>
        <div className="ph-form-row ph-form-row--full">
          <span className="ph-field-label">{t("fields.assignee")}</span>
          <Select
            value={newAssigneeId}
            onChange={setNewAssigneeId}
            className="ph-field-select ph-field-select--full"
            placeholder={t("addTaskModal.assigneePlaceholder")}
            allowClear
            options={assigneeOptions.map((m) => ({
              value: m.id,
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: avatarColor(m.id),
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {m.username.charAt(0).toUpperCase()}
                  </span>
                  {m.username}
                </span>
              ),
            }))}
          />
        </div>
        <div className="ph-form-row ph-form-row--full">
          <span className="ph-field-label">{t("fields.linkedTool")}</span>
          <Select
            value={newLinkedTool}
            onChange={setNewLinkedTool}
            className="ph-field-select ph-field-select--full"
            placeholder={t("common.empty")}
            allowClear
            options={LINKED_TOOLS.map((t) => ({ value: t, label: t }))}
          />
        </div>
      </div>
      <div className="ph-desc-box">
        <div className="ph-desc-toolbar">
          <button
            type="button"
            className="ph-desc-btn"
            onClick={() => formatDesc(newDescRef, "bold")}
          >
            <BoldOutlined />
          </button>
          <button
            type="button"
            className="ph-desc-btn"
            onClick={() => formatDesc(newDescRef, "italic")}
          >
            <ItalicOutlined />
          </button>
          <button
            type="button"
            className="ph-desc-btn"
            onClick={() => formatDesc(newDescRef, "underline")}
          >
            <UnderlineOutlined />
          </button>
          <button
            type="button"
            className="ph-desc-btn"
            onClick={() => insertLinkDesc(newDescRef)}
          >
            <LinkOutlined />
          </button>
          <button
            type="button"
            className="ph-desc-btn"
            onClick={() => insertImageDesc(newDescRef)}
          >
            <PaperClipOutlined />
          </button>
        </div>
        <ReactQuill
          ref={newDescRef}
          value={newDescription}
          onChange={setNewDescription}
          modules={quillModules}
          className="ph-desc-quill"
          placeholder={t("common.descriptionPlaceholder")}
        />
      </div>
      <div className="ph-modal-actions">
        <button
          className="ph-btn-cancel"
          onClick={() => {
            setShowAddTask(false);
            resetAddForm();
          }}
        >
          {t("common.cancel")}
        </button>
        <button
          className="ph-btn-create"
          onClick={handleCreateTask}
          disabled={creating}
        >
          {creating ? t("addTaskModal.creating") : t("addTaskModal.createTask")}
        </button>
      </div>
    </Modal>
  );

  // ── Task preview ──────────────────────────────────────────────────────────

  const renderTaskPreview = () => {
    if (!selectedTask) return null;
    const col = columns.find((c) => c.id === selectedTask.status_id);
    const prioConf = PRIORITY_CONFIG[selectedTask.priority];

    return (
      <Modal
        open={!!selectedTask}
        onCancel={closeTask}
        footer={null}
        title={null}
        width={560}
        className="ph-antd-modal"
        destroyOnClose
      >
        {/* Header row */}
        <div className="ph-preview-header-row">
          <div className="ph-preview-updated">
            {t("taskPreview.lastUpdated", {
              date: dayjs(
                selectedTask.modified_at ?? selectedTask.created_at,
              ).format("MMM D, YYYY"),
            })}
          </div>
          {!editMode ? (
            <button className="ph-edit-btn" onClick={() => setEditMode(true)}>
              <EditOutlined /> {t("common.edit")}
            </button>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="ph-edit-btn ph-edit-btn--cancel"
                onClick={() => setEditMode(false)}
              >
                <CloseOutlined /> {t("common.cancel")}
              </button>
              <button
                className="ph-edit-btn ph-edit-btn--save"
                onClick={handleSaveTask}
                disabled={saving}
              >
                <SaveOutlined /> {saving ? t("taskPreview.saving") : t("common.save")}
              </button>
            </div>
          )}
        </div>

        {/* Title */}
        {editMode ? (
          <Input
            className="ph-preview-title-input"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
          />
        ) : (
          <>
            <h2 className="ph-preview-title">{selectedTask.title}</h2>
            <p className="ph-preview-created-by">
              {t("taskPreview.createdBy", {
                username: selectedTask.created_by_username,
              })}
            </p>
          </>
        )}

        {/* Fields grid */}
        <div className="ph-preview-fields">
          {/* Status */}
          <div className="ph-preview-field">
            <span className="ph-preview-field-label">{t("fields.status")}</span>
            {editMode ? (
              <Select
                value={editStatusId}
                onChange={setEditStatusId}
                className="ph-field-select"
                popupMatchSelectWidth={false}
                options={columns.map((c) => ({
                  value: c.id,
                  label: (
                    <span>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: c.color,
                          marginRight: 6,
                        }}
                      />
                      {c.name}
                    </span>
                  ),
                }))}
              />
            ) : col ? (
              <span
                className="ph-status-badge"
                style={{ color: col.color, background: col.color + "20" }}
              >
                ● {col.name}
              </span>
            ) : (
              <span className="ph-preview-empty">—</span>
            )}
          </div>

          {/* Priority */}
          <div className="ph-preview-field">
            <span className="ph-preview-field-label">{t("fields.priority")}</span>
            {editMode ? (
              <Select
                value={editPriority}
                onChange={setEditPriority}
                className="ph-field-select"
                popupMatchSelectWidth={false}
                options={(
                  ["low", "medium", "high", "urgent"] as ApiPriority[]
                ).map((p) => ({
                  value: p,
                  label: (
                    <span
                      style={{
                        color: PRIORITY_CONFIG[p].color,
                        fontWeight: 600,
                      }}
                    >
                      {priorityLabel(p)}
                    </span>
                  ),
                }))}
              />
            ) : (
              <span
                className="ph-priority-badge"
                style={{ color: prioConf.color, background: prioConf.bg }}
              >
                {priorityLabel(selectedTask.priority)}
              </span>
            )}
          </div>

          {/* Milestone */}
          <div className="ph-preview-field">
            <span className="ph-preview-field-label">{t("fields.milestone")}</span>
            <Checkbox
              checked={editMode ? editMilestone : selectedTask.is_milestone}
              onChange={(e) => editMode && setEditMilestone(e.target.checked)}
              disabled={!editMode}
            />
          </div>

          {/* Due date */}
          <div className="ph-preview-field">
            <span className="ph-preview-field-label">{t("fields.dueDate")}</span>
            {editMode ? (
              <DatePicker
                value={editDueDate}
                onChange={setEditDueDate}
                placeholder={t("common.empty")}
                className="ph-field-date"
              />
            ) : selectedTask.due_date ? (
              <span>{dayjs(selectedTask.due_date).format("MMMM D, YYYY")}</span>
            ) : (
              <span className="ph-preview-empty">{t("common.empty")}</span>
            )}
          </div>

          {/* Assignee */}
          <div className="ph-preview-field">
            <span className="ph-preview-field-label">{t("fields.assignee")}</span>
            {editMode ? (
              <Select
                value={editAssigneeId}
                onChange={setEditAssigneeId}
                className="ph-field-select"
                placeholder={t("common.empty")}
                allowClear
                options={assigneeOptions.map((m) => ({
                  value: m.id,
                  label: (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: avatarColor(m.id),
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 600,
                        }}
                      >
                        {m.username.charAt(0).toUpperCase()}
                      </span>
                      {m.username}
                    </span>
                  ),
                }))}
              />
            ) : selectedTask.assignee ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  className="ph-avatar"
                  style={{ background: avatarColor(selectedTask.assignee.id) }}
                >
                  {selectedTask.assignee.username.charAt(0).toUpperCase()}
                </div>
                <span style={{ fontSize: 13 }}>
                  {selectedTask.assignee.username}
                </span>
              </div>
            ) : (
              <span className="ph-preview-empty">{t("common.empty")}</span>
            )}
          </div>

          {/* Linked tool */}
          <div className="ph-preview-field">
            <span className="ph-preview-field-label">{t("fields.linkedTool")}</span>
            {editMode ? (
              <Select
                value={editLinkedTool}
                onChange={setEditLinkedTool}
                className="ph-field-select"
                placeholder={t("common.empty")}
                allowClear
                options={LINKED_TOOLS.map((tool) => ({ value: tool, label: tool }))}
              />
            ) : selectedTask.linked_tool ? (
              <span className="ph-tool-badge">{selectedTask.linked_tool}</span>
            ) : (
              <span className="ph-preview-empty">{t("common.empty")}</span>
            )}
          </div>
        </div>

        {/* Description */}
        {editMode ? (
          <div className="ph-desc-box">
            <div className="ph-desc-toolbar">
              <button
                type="button"
                className="ph-desc-btn"
                onClick={() => formatDesc(editDescRef, "bold")}
              >
                <BoldOutlined />
              </button>
              <button
                type="button"
                className="ph-desc-btn"
                onClick={() => formatDesc(editDescRef, "italic")}
              >
                <ItalicOutlined />
              </button>
              <button
                type="button"
                className="ph-desc-btn"
                onClick={() => formatDesc(editDescRef, "underline")}
              >
                <UnderlineOutlined />
              </button>
              <button
                type="button"
                className="ph-desc-btn"
                onClick={() => insertLinkDesc(editDescRef)}
              >
                <LinkOutlined />
              </button>
              <button
                type="button"
                className="ph-desc-btn"
                onClick={() => insertImageDesc(editDescRef)}
              >
                <PaperClipOutlined />
              </button>
            </div>
            <ReactQuill
              ref={editDescRef}
              value={editDescription}
              onChange={setEditDescription}
              modules={quillModules}
              className="ph-desc-quill"
              placeholder={t("common.descriptionPlaceholder")}
            />
          </div>
        ) : selectedTask.description ? (
          <div
            className="ph-preview-desc-html"
            dangerouslySetInnerHTML={{ __html: selectedTask.description }}
          />
        ) : (
          <p className="ph-preview-desc-placeholder">
            {t("common.descriptionPlaceholder")}
          </p>
        )}

        {/* Attachments */}
        <div className="ph-attachments">
          <div className="ph-attachments-header">
            <h4 className="ph-comments-header" style={{ margin: 0 }}>
              {t("attachments.header")}{" "}
              <span className="ph-comments-count">{attachments.length}</span>
            </h4>
            <button
              className="ph-attach-add-btn"
              onClick={() => setShowAddAttach((v) => !v)}
            >
              <PlusOutlined /> {t("attachments.addLink")}
            </button>
          </div>

          {showAddAttach && (
            <div className="ph-attach-form">
              <Input
                placeholder={t("attachments.fileNamePlaceholder")}
                value={newAttachName}
                onChange={(e) => setNewAttachName(e.target.value)}
                className="ph-attach-input"
              />
              <Input
                placeholder={t("attachments.urlPlaceholder")}
                value={newAttachUrl}
                onChange={(e) => setNewAttachUrl(e.target.value)}
                className="ph-attach-input"
              />
              <div
                style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
              >
                <button
                  className="ph-btn-cancel"
                  style={{ padding: "5px 14px", fontSize: 13 }}
                  onClick={() => {
                    setShowAddAttach(false);
                    setNewAttachName("");
                    setNewAttachUrl("");
                  }}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="ph-btn-create"
                  style={{ padding: "5px 14px", fontSize: 13 }}
                  onClick={handleAddAttachment}
                  disabled={addingAttach}
                >
                  {addingAttach ? t("attachments.adding") : t("common.add")}
                </button>
              </div>
            </div>
          )}

          {attachmentsLoading ? (
            <div style={{ padding: "12px 0" }}>
              <Spin size="small" />
            </div>
          ) : attachments.length > 0 ? (
            <div className="ph-attach-list">
              {attachments.map((a) => (
                <div key={a.id} className="ph-attach-item">
                  <PaperClipOutlined className="ph-attach-icon" />
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ph-attach-name"
                  >
                    {a.file_name}
                  </a>
                  <button
                    className="ph-attach-delete"
                    onClick={() => handleDeleteAttachment(a.id)}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Comments */}
        <div className="ph-comments">
          <h4 className="ph-comments-header">
            {t("comments.header")}{" "}
            <span className="ph-comments-count">{taskComments.length}</span>
          </h4>
          {commentsLoading ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Spin />
            </div>
          ) : taskComments.length === 0 ? (
            <div className="ph-comments-empty">
              <MessageOutlined className="ph-comments-empty-icon" />
              <p>{t("comments.emptyTitle")}</p>
              <p>{t("comments.emptySubtitle")}</p>
            </div>
          ) : (
            <div className="ph-comments-list">
              {taskComments.map((c) => (
                <div key={c.id} className="ph-comment">
                  <div
                    className="ph-comment-avatar"
                    style={{ background: avatarColor(c.user_id) }}
                  >
                    {c.username.charAt(0).toUpperCase()}
                  </div>
                  <div className="ph-comment-body">
                    <div className="ph-comment-meta">
                      <span className="ph-comment-author">{c.username}</span>
                      <span className="ph-comment-date">
                        {dayjs(c.created_at).format("MMM D, YYYY")}
                      </span>
                    </div>
                    <p className="ph-comment-text">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="ph-comment-input-row">
            <Input
              placeholder={t("comments.inputPlaceholder")}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onPressEnter={handleSendComment}
              className="ph-comment-input"
              disabled={commenting}
              suffix={
                <button
                  className="ph-send-btn"
                  onClick={handleSendComment}
                  disabled={commenting}
                >
                  <SendOutlined />
                  &nbsp;{t("comments.send")}
                </button>
              }
            />
          </div>
        </div>
      </Modal>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="project-hub">
      <div className="ph-card">
        <h1 className="ph-title">{t("title")}</h1>

        <div className="ph-tabs">
          {(["board", "milestones", "deadlines"] as const).map((tab) => (
            <button
              key={tab}
              className={`ph-tab ${activeTab === tab ? "ph-tab--active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {t(`tabs.${tab}`)}
            </button>
          ))}
        </div>

        <div className="ph-toolbar">
          <div className="ph-filters">
            <Select
              value={filterAssignee}
              onChange={setFilterAssignee}
              placeholder={t("filters.assigneePlaceholder")}
              allowClear
              className="ph-filter-select"
              options={assigneeOptions.map((m) => ({
                value: String(m.id),
                label: m.username,
              }))}
            />
            <Select
              value={filterPriority}
              onChange={setFilterPriority}
              placeholder={t("filters.priorityPlaceholder")}
              allowClear
              className="ph-filter-select"
              options={(
                ["low", "medium", "high", "urgent"] as ApiPriority[]
              ).map((p) => ({ value: p, label: priorityLabel(p) }))}
            />
            <Select
              value={filterTool}
              onChange={setFilterTool}
              placeholder={t("filters.linkedToolPlaceholder")}
              allowClear
              className="ph-filter-select"
              options={LINKED_TOOLS.map((tool) => ({ value: tool, label: tool }))}
            />
          </div>
          <button
            className="ph-add-task-btn"
            onClick={() => setShowAddTask(true)}
          >
            <PlusOutlined /> {t("common.addTask")}
          </button>
        </div>

        {activeTab === "board" && renderBoard()}
        {activeTab === "milestones" && renderMilestones()}
        {activeTab === "deadlines" && renderDeadlines()}
      </div>

      {renderAddTaskModal()}
      {renderTaskPreview()}

      {/* Delete confirmation modal */}
      <Modal
        open={!!deleteTargetId}
        onCancel={() => setDeleteTargetId(null)}
        footer={null}
        width={400}
        centered
        className="ph-antd-modal"
        destroyOnClose
      >
        <div className="ph-delete-modal">
          <div className="ph-delete-modal-icon">
            <ExclamationCircleOutlined />
          </div>
          <h3 className="ph-delete-modal-title">{t("deleteModal.title")}</h3>
          <p className="ph-delete-modal-text">
            {t("deleteModal.text")}
          </p>
          <div className="ph-delete-modal-actions">
            <button className="ph-btn-cancel" onClick={() => setDeleteTargetId(null)}>
              {t("common.cancel")}
            </button>
            <button className="ph-btn-delete" onClick={confirmDelete} disabled={deleteConfirmLoading}>
              {deleteConfirmLoading ? t("deleteModal.deleting") : t("common.delete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectHub;
